import React, { useState } from 'react';
import { Calculator, Calendar, Clock, MapPin, ArrowRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const KUNDLI_BG = '/media/stock/c9b776d4_photo-1729335511883-29eade10006b.jpg';

export default function KundliCalculator() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '', birth_date: '', birth_time: '', birth_place: ''
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.birth_date) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/kundli`, formData);
      // Navigate to premium report page
      navigate('/kundli/report', { state: { kundli: res.data, formData } });
      setResult(res.data);
    } catch (err) {
      setError('Calculation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kundli" className="section-padding relative overflow-hidden" data-testid="kundli-section">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={KUNDLI_BG} alt="" className="w-full h-full object-cover opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
      </div>
      <div className="absolute top-1/3 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs tracking-[0.3em] uppercase text-gold font-semibold font-body">
            Free Kundli | निःशुल्क कुंडली
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-text-main mt-4 mb-6 tracking-tight">
            Online Kundli <span className="text-gold-gradient">Calculator</span>
          </h2>
          <p className="text-text-muted">
            Get your Vedic birth chart analysis instantly with accurate Ascendant, Nakshatra & more.
          </p>
          <p className="text-text-muted/70 text-sm mt-2">
            सटीक लग्न, नक्षत्र और अधिक के साथ वैदिक जन्म कुंडली तुरंत प्राप्त करें।
          </p>

          {/* Gun Milan CTA */}
          <a href="/gun-milan"
            className="inline-flex items-center gap-2 mt-5 px-6 py-3 bg-gradient-to-r from-pink-600/20 to-purple-600/20 border border-pink-500/20 rounded-full text-pink-300 hover:text-pink-200 hover:border-pink-400/30 transition-all group"
            data-testid="gun-milan-cta">
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
            गुण मिलान / Gun Milan (Matrimonial)
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        <div className="max-w-4xl mx-auto">
          {!result ? (
            <div className="glass-card p-8 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-text-muted mb-2 font-medium">Name / नाम *</label>
                    <input
                      type="text" value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Enter your name / अपना नाम लिखें" required
                      data-testid="kundli-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-text-muted mb-2 font-medium">Birth Date / जन्म तिथि *</label>
                    <div className="relative">
                      <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                      <input
                        type="date" value={formData.birth_date}
                        onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                        required className="pl-12" data-testid="kundli-date-input"
                      />
                    </div>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-text-muted mb-2 font-medium">Birth Time / जन्म समय</label>
                    <div className="relative">
                      <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                      <input
                        type="time" value={formData.birth_time}
                        onChange={(e) => setFormData({...formData, birth_time: e.target.value})}
                        className="pl-12" data-testid="kundli-time-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-text-muted mb-2 font-medium">Birth Place / जन्म स्थान</label>
                    <div className="relative">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                      <input
                        type="text" value={formData.birth_place}
                        onChange={(e) => setFormData({...formData, birth_place: e.target.value})}
                        placeholder="City name / शहर का नाम" className="pl-12"
                        data-testid="kundli-place-input"
                      />
                    </div>
                    <p className="text-xs text-text-muted/50 mt-1">e.g. Delhi, Mumbai, Chandigarh, Jaipur</p>
                  </div>
                </div>
                {error && <p className="text-red-400 text-sm text-center">{error}</p>}
                <button
                  type="submit" disabled={loading || !formData.birth_date || !formData.name}
                  className="btn-gold w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="kundli-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Calculating... | गणना हो रही है...</>
                  ) : (
                    <><Calculator className="w-5 h-5" /> Generate Kundli | कुंडली बनाएं</>
                  )}
                </button>
              </form>
            </div>
          ) : (
            <div className="space-y-6 animate-fade-in-up">
              {/* Main Result Card */}
              <div className="glass-card p-8">
                <div className="text-center mb-8">
                  <h3 className="font-heading text-3xl text-text-main mb-2">
                    {result.name} की कुंडली
                  </h3>
                  <p className="text-text-muted text-sm">
                    Birth: {result.birthDate} | {result.birthTime} | {result.birthPlace}
                  </p>
                </div>

                {/* Zodiac Display - 3 Main Signs */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl p-6 text-center border border-primary/20" data-testid="kundli-sun-sign">
                    <p className="text-6xl mb-3">{result.sunSign.symbol}</p>
                    <p className="font-heading text-xl text-text-main">{result.sunSign.name}</p>
                    <p className="text-primary font-medium">{result.sunSign.nameHi}</p>
                    <p className="text-xs text-text-muted mt-2">Sun Sign / सूर्य राशि</p>
                  </div>
                  <div className="bg-gradient-to-br from-cosmic-blue/30 to-cosmic-blue/10 rounded-2xl p-6 text-center border border-cosmic-blue/20" data-testid="kundli-moon-sign">
                    <p className="text-6xl mb-3">{result.moonSign.symbol}</p>
                    <p className="font-heading text-xl text-text-main">{result.moonSign.name}</p>
                    <p className="text-primary font-medium">{result.moonSign.nameHi}</p>
                    <p className="text-xs text-text-muted mt-2">Moon Sign / चंद्र राशि</p>
                  </div>
                  <div className="bg-gradient-to-br from-cosmic-purple/30 to-cosmic-purple/10 rounded-2xl p-6 text-center border border-cosmic-purple/20" data-testid="kundli-ascendant">
                    <p className="text-6xl mb-3">{result.ascendant.symbol}</p>
                    <p className="font-heading text-xl text-text-main">{result.ascendant.name}</p>
                    <p className="text-primary font-medium">{result.ascendant.nameHi}</p>
                    <p className="text-xs text-text-muted mt-2">Ascendant / लग्न ({result.ascendantDegree}°)</p>
                  </div>
                </div>

                {/* Additional Details */}
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { label: 'Nakshatra / नक्षत्र', value: `${result.nakshatra} (Pada ${result.nakshatraPada})` },
                    { label: 'Lucky Day / शुभ दिन', value: result.luckyDay },
                    { label: 'Lucky Color / शुभ रंग', value: result.luckyColor },
                    { label: 'Lucky Numbers / शुभ अंक', value: result.luckyNumbers },
                  ].map((item, idx) => (
                    <div key={idx} className="bg-surface-alt/80 rounded-xl p-4 border border-primary/10">
                      <p className="text-sm text-text-muted mb-1">{item.label}</p>
                      <p className="font-heading text-lg text-text-main">{item.value}</p>
                    </div>
                  ))}
                </div>

                {/* Baby Names Section */}
                {result.babyNames && result.babyNames.letters && result.babyNames.letters.length > 0 && (
                  <div className="mt-8" data-testid="baby-names-section">
                    <h4 className="font-heading text-2xl text-text-main mb-4 text-center">
                      शिशु नामावली <span className="text-gold-gradient">Baby Names</span>
                    </h4>
                    <p className="text-text-muted text-sm text-center mb-6">
                      {result.nakshatra} नक्षत्र (Pada {result.nakshatraPada}) के अनुसार शुभ नाम अक्षर
                    </p>

                    {/* Name Letters */}
                    <div className="flex flex-wrap justify-center gap-3 mb-6">
                      {result.babyNames.letters.map((letter, idx) => (
                        <div key={idx} className="w-14 h-14 rounded-xl bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/20 flex items-center justify-center">
                          <span className="font-heading text-xl text-gold font-medium">{letter}</span>
                        </div>
                      ))}
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-xl p-5 border border-blue-500/10">
                        <p className="text-blue-300 text-xs uppercase tracking-wider mb-3 font-semibold">Boys / लड़कों के नाम</p>
                        <div className="flex flex-wrap gap-2">
                          {result.babyNames.names_boy.map((name, idx) => (
                            <span key={idx} className="px-3 py-1.5 bg-blue-500/10 rounded-lg text-text-main text-sm border border-blue-500/10">{name}</span>
                          ))}
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-pink-500/10 to-pink-500/5 rounded-xl p-5 border border-pink-500/10">
                        <p className="text-pink-300 text-xs uppercase tracking-wider mb-3 font-semibold">Girls / लड़कियों के नाम</p>
                        <div className="flex flex-wrap gap-2">
                          {result.babyNames.names_girl.map((name, idx) => (
                            <span key={idx} className="px-3 py-1.5 bg-pink-500/10 rounded-lg text-text-main text-sm border border-pink-500/10">{name}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Vimshottari Dasha Section */}
                {result.dashas && result.dashas.length > 0 && (
                  <div className="mt-8" data-testid="dasha-section">
                    <h4 className="font-heading text-2xl text-text-main mb-2 text-center">
                      विमशोत्तरी <span className="text-gold-gradient">दशा</span>
                    </h4>
                    <p className="text-text-muted text-sm text-center mb-6">
                      Vimshottari Dasha - 120 वर्ष का ग्रह चक्र
                    </p>
                    <div className="space-y-2">
                      {result.dashas.map((dasha, idx) => (
                        <div key={idx}
                          className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                            dasha.active 
                              ? 'bg-gold/10 border-gold/30 ring-1 ring-gold/20' 
                              : 'bg-white/3 border-white/5'
                          }`}
                          data-testid={`dasha-${idx}`}>
                          <div className="flex items-center gap-3">
                            {dasha.active && <span className="w-2 h-2 bg-gold rounded-full animate-pulse" />}
                            <div>
                              <p className={`text-sm font-medium ${dasha.active ? 'text-gold' : 'text-text-main'}`}>
                                {dasha.lord} {dasha.active && '(चालू)'}
                              </p>
                              <p className="text-xs text-text-muted">{dasha.years} वर्ष</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-text-muted">
                              {new Date(dasha.start).toLocaleDateString('hi-IN', {year:'numeric',month:'short'})} — {new Date(dasha.end).toLocaleDateString('hi-IN', {year:'numeric',month:'short'})}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="bg-gradient-to-r from-gold to-gold-dark rounded-2xl p-8 text-background text-center">
                <h4 className="font-heading text-2xl mb-3">
                  Want Detailed Analysis? | विस्तृत विश्लेषण चाहिए?
                </h4>
                <p className="mb-6 opacity-90">
                  Vivek Mudgal Astrologer se complete Kundli with Dasha, Yoga, and personalized remedies paayein.
                  <br/>
                  <span className="text-sm">पं. विवेक मुद्गल जी से पूर्ण कुंडली, दशा, योग और व्यक्तिगत उपाय प्राप्त करें।</span>
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <a href="#contact" className="bg-background text-gold px-6 py-3 rounded-full font-semibold hover:bg-surface transition-colors inline-flex items-center gap-2">
                    Book Consultation <ArrowRight className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => setResult(null)}
                    className="border-2 border-background text-background px-6 py-3 rounded-full font-semibold hover:bg-background/10 transition-colors"
                    data-testid="kundli-recalculate-btn"
                  >
                    Calculate Again
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
